import { memo, useState, useCallback, useEffect, useRef } from "react";
import { useI18n } from "@/i18n";
import { Pagination } from "@/design-system";
import { listInventoryStock } from "@/infrastructure/inventory/inventoryStockService";
import { listTypeUnits } from "@/infrastructure/type-unit/typeUnitService";
import { listProducts } from "@/infrastructure/product/productService";
import { listCategories } from "@/infrastructure/category/categoryService";
import type { InventoryStock, ProductMeta, TypeUnit, Product, Category } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";
import { formatDate } from "@/utils/formatDate";

const PAGE_SIZE = 8;

export const InventoryPage = memo(function InventoryPage() {
  const { t } = useI18n();
  const [items, setItems] = useState<InventoryStock[]>([]);
  const [meta, setMeta] = useState<ProductMeta>({ totalItems: 0, itemsPerPage: PAGE_SIZE, totalPages: 0, currentPage: 1 });
  const [units, setUnits] = useState<TypeUnit[]>([]);
  const [products, setProducts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [stockData, unitData, prodData] = await Promise.all([
        listInventoryStock({ page, limit: PAGE_SIZE, search: debouncedSearch || undefined, categoryId: categoryFilter || undefined }),
        units.length === 0 ? listTypeUnits() : Promise.resolve(units),
        Object.keys(products).length === 0 ? listProducts({ limit: 100 }).then(r => r.items) : Promise.resolve([] as Product[]),
      ]);
      setItems(stockData.items);
      setMeta(stockData.meta);
      if (units.length === 0) setUnits(unitData as TypeUnit[]);
      if (Object.keys(products).length === 0 && Array.isArray(prodData) && prodData.length > 0) {
        const map: Record<string, string> = {};
        (prodData as Product[]).forEach(p => { map[p.id] = `${p.code} — ${p.name}`; });
        setProducts(map);
      }
    } catch {}
    setLoading(false);
  }, [page, debouncedSearch, categoryFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => { listCategories({ limit: 100, status: "ACTIVE" }).then(res => setCategories(res.items)).catch(() => {}); }, []);

  const getUnit = useCallback((id: string) => units.find(u => u.id === id)?.abbreviation ?? "", [units]);
  const getProduct = useCallback((id: string, code?: string, name?: string) => {
    if (code) return code;
    return products[id] ?? id.slice(0, 8);
  }, [products]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div>
        <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.inventory.title} ({meta.totalItems})</h1>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>{t.inventory.subtitle}</p>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t.inventory.searchPlaceholder} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", maxWidth: "300px", boxSizing: "border-box" }} />
        <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: categoryFilter ? "#111" : "#666", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", cursor: "pointer" }}>
          <option value="">{t.products.filterAllCategories}</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", overflow: "hidden", minHeight: "300px" }}>
        {loading && <LoadingState />}
        {!loading && items.length === 0 && <EmptyState />}
        {!loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.inventory.colProduct, t.inventory.colOnHand, t.inventory.colReserved, t.inventory.colAvailable, t.inventory.colUnit, t.products.colCategory, t.inventory.colLot, t.inventory.colLastMovement].map(col => (
                    <th key={col} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 16px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} style={{ borderBottom: "1px solid #f7f7f7", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")} onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111" }}>{getProduct(item.productId, item.productCode, item.productName)}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{item.quantityOnHand}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: item.quantityReserved > 0 ? "#f59e0b" : "#bbb" }}>{item.quantityReserved}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#16a34a" }}>{item.quantityAvailable}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{getUnit(item.typeUnitId)}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{item.categoryName ?? "—"}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#bbb" }}>{item.lotNumber === "DEFAULT" ? "—" : item.lotNumber}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#bbb" }}>{formatDate(item.lastMovementAt)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {meta.totalPages > 1 && (
              <div style={{ padding: "0 16px 14px" }}>
                <Pagination currentPage={meta.currentPage} totalPages={meta.totalPages} totalItems={meta.totalItems} onPageChange={setPage} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

const cell: React.CSSProperties = { padding: "12px 16px", verticalAlign: "middle" };

function EmptyState() {
  const { t } = useI18n();
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", gap: "10px", textAlign: "center" }}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb" }}>{t.inventory.emptyTitle}</span></div>;
}

function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}
